import React, { useLayoutEffect, useRef } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd";
import type { Rule } from "antd/es/form";

import { Form, Input, Select } from "../../../../components/shared";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import featureFlagService from "../../../../services/FeatureFlagService";
import { CASINO_PROVIDER_DEEPLINK_COLLECTION_SLUG } from "../../../../queries/dynamicBonuses/casinoDeeplinkConstants";
import type { CasinoDeeplinkDropdownItem } from "../../../../queries/dynamicBonuses/useCasinoDeeplinkDropdown";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { useSportsbookRestrictionNameFromCache } from "../../../../queries/dynamicBonuses/useSportsbookRestrictionDropdown";
import type { SportsbookSearchType } from "../constants";
import type {
  BannerDeeplinkIn,
  BannerDeeplinkScreen,
  BannerIn,
  ConditionSpecIn,
} from "../types";
import SportsbookDeeplinkIdSelect from "./SportsbookDeeplinkIdSelect";
import {
  suggestBannerDeeplink,
  suggestionMatchesDeeplink,
  type DeeplinkSuggestion,
} from "./deeplinkSuggestion";
import {
  RESTRICTION_DISPLAY_NAMES_PARAM_KEY,
  rememberRestrictionDisplayName,
  getRestrictionDisplayName,
  usableRestrictionDisplayName,
  useRestrictionDisplayName,
} from "./restrictionDisplayNames";
import CasinoDeeplinkSearchField from "./CasinoDeeplinkSearchField";
import styles from "../stylesDynamicBonuses.module.css";

type NamePath = (string | number)[];
type DeeplinkConfig = Partial<BannerDeeplinkIn>;

/** `Form.useWatch(undefined)` subscribes to the whole form store. */
const IDLE_WATCH_PATH: NamePath = ["__deeplink_suggestion_idle__"];

type Props = {
  form: FormInstance;
  namePath: NamePath;
  itemNamePath: NamePath;
  resultParamsPath?: NamePath;
  conditionsPath?: NamePath;
  restrictionDisplayNames?: Record<string, string>;
};

const SCREEN_OPTIONS = (): { label: string; value: BannerDeeplinkScreen }[] => [
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_SPORTSBOOK_LEAGUE,
    value: "sportsbook_league",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_SPORTSBOOK,
    value: "sportsbook",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_SPORTSBOOK_MATCH,
    value: "sportsbook_match",
  },
  { label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_CASINO, value: "casino" },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_CASINO_GAME,
    value: "casino_game",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_CASINO_PROVIDER,
    value: "casino_provider",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_CASINO_COLLECTION,
    value: "casino_collection",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_CASINO_CATEGORY,
    value: "casino_category",
  },
  { label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_DEPOSIT, value: "deposit" },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_USER_PROFILE,
    value: "user_profile",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_POKER_TABLE,
    value: "poker_table",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_POKER_LOBBY,
    value: "poker_lobby",
  },
  {
    label: AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_GAME_PASS,
    value: "game_pass",
  },
];

const valueOf = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

const decoded = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const parseLegacyDeeplink = (deeplink: string): DeeplinkConfig => {
  const value = valueOf(deeplink);
  if (!value) return {};

  if (value === "/sportsbook") return { screen: "sportsbook" };
  if (value.startsWith("/sportsbook?")) {
    const params = new URLSearchParams(value.split("?")[1]);
    return {
      screen: "sportsbook_league",
      tournament_id: params.get("pendingLeagueId") ?? undefined,
      sport_id: params.get("pendingSportId") ?? undefined,
    };
  }
  if (value.startsWith("/sportsbook/event/")) {
    return {
      screen: "sportsbook_match",
      event_id: decoded(value.slice("/sportsbook/event/".length)),
    };
  }
  if (value.startsWith("/profile?openDeposit=")) {
    const moneyType = new URLSearchParams(value.split("?")[1]).get("openDeposit");
    return {
      screen: "deposit",
      money_type: moneyType === "coin" ? "coin" : "cash",
    };
  }
  if (value === "/profile") return { screen: "user_profile" };
  if (value === "/social-casino" || value === "/social-casino-v2") {
    return { screen: "casino" };
  }
  if (value.startsWith("/casino/")) {
    return {
      screen: "casino_game",
      game_id: decoded(value.slice("/casino/".length)),
    };
  }

  const providerMatch = value.match(
    /^\/social-casino-v2\/([^/]+)\/provider\/([^/]+)$/,
  );
  if (providerMatch) {
    return {
      screen: "casino_provider",
      code: decoded(providerMatch[1]),
      provider_id: decoded(providerMatch[2]),
    };
  }
  const casinoCatalogMatch = value.match(/^\/social-casino-v2\/([^/]+)$/);
  if (casinoCatalogMatch) {
    return {
      screen: "casino_collection",
      code: decoded(casinoCatalogMatch[1]),
    };
  }
  if (value.startsWith("/social-casino?")) {
    const params = new URLSearchParams(value.split("?")[1]);
    const eid = params.get("casinoGroupId") ?? undefined;
    const code = params.get("casinoGroupName") ?? undefined;
    if (eid || code) return { screen: "casino_collection", eid, code };
  }
  if (value === "/poker-v2") return { screen: "poker_lobby" };
  if (value.startsWith("/poker-v2/")) {
    return {
      screen: "poker_table",
      game_id: decoded(value.slice("/poker-v2/".length)),
    };
  }
  if (value === "/game-pass-v2/" || value === "/game-pass-v2") {
    return { screen: "game_pass" };
  }

  return {};
};

const initialConfigFrom = (
  deeplink?: BannerIn["deeplink"],
): DeeplinkConfig => {
  if (!deeplink) return {};
  return typeof deeplink === "string"
    ? parseLegacyDeeplink(deeplink)
    : deeplink;
};

type AutoAppliedSuggestion = {
  kind: DeeplinkSuggestion["kind"];
  id: string;
};

const screenForSuggestionKind = (
  kind: DeeplinkSuggestion["kind"],
): BannerDeeplinkScreen => {
  switch (kind) {
    case "casino_game":
      return "casino_game";
    case "league":
      return "sportsbook_league";
    case "match":
      return "sportsbook_match";
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};

const idFromDeeplinkForKind = (
  deeplink: BannerDeeplinkIn,
  kind: DeeplinkSuggestion["kind"],
): string | undefined => {
  switch (kind) {
    case "casino_game":
      return valueOf(deeplink.game_id);
    case "league":
      return valueOf(deeplink.tournament_id);
    case "match":
      return valueOf(deeplink.event_id);
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};

const deeplinkMatchesAutoApplied = (
  deeplink: BannerIn["deeplink"] | undefined,
  applied: AutoAppliedSuggestion | undefined,
): boolean => {
  if (!applied || !deeplink || typeof deeplink === "string") return false;
  return (
    deeplink.screen === screenForSuggestionKind(applied.kind) &&
    idFromDeeplinkForKind(deeplink, applied.kind) === applied.id
  );
};

const buildDeeplinkFromSuggestion = (
  suggestion: DeeplinkSuggestion,
  includeCasinoProviderId: boolean,
): BannerDeeplinkIn => {
  if (suggestion.kind === "casino_game") {
    return {
      screen: "casino_game",
      game_id: suggestion.deeplink.game_id,
      ...(includeCasinoProviderId && suggestion.provider
        ? { provider_id: suggestion.provider }
        : {}),
    };
  }
  return suggestion.deeplink;
};

const canAutoApplySuggestion = (
  suggestion: DeeplinkSuggestion,
  deeplink: BannerIn["deeplink"] | undefined,
  dismissedId: string | undefined,
  lastAutoApplied: AutoAppliedSuggestion | undefined,
): boolean => {
  if (dismissedId === suggestion.id) return false;

  // Unset / blank only. A non-empty legacy string is an existing manual
  // destination — require Remove before auto-apply (BONENG-1163 review).
  if (!deeplink) return true;
  if (typeof deeplink === "string") return !deeplink.trim();
  if (!deeplink.screen) return true;

  // Still holding a previous auto-apply — allow switching league ↔ match ↔ casino.
  if (deeplinkMatchesAutoApplied(deeplink, lastAutoApplied)) return true;

  const expectedScreen = screenForSuggestionKind(suggestion.kind);
  if (deeplink.screen !== expectedScreen) return false;

  const currentId = idFromDeeplinkForKind(deeplink, suggestion.kind);
  if (!currentId) return true;
  return currentId === lastAutoApplied?.id;
};

const BannerDeeplinkField: React.FC<Props> = ({
  form,
  namePath,
  itemNamePath,
  resultParamsPath,
  conditionsPath,
  restrictionDisplayNames,
}) => {
  const deeplinkPath = [...namePath, "deeplink"];
  const itemDeeplinkPath = [...itemNamePath, "deeplink"];
  const initialDeeplink = form.getFieldValue(
    deeplinkPath,
  ) as BannerIn["deeplink"];
  const initialConfig = initialConfigFrom(initialDeeplink);
  const legacyInitialValue =
    typeof initialDeeplink === "string" ? initialConfig : undefined;
  const watchedDeeplink = Form.useWatch(deeplinkPath, {
    form,
    preserve: true,
  }) as BannerIn["deeplink"];
  const config =
    watchedDeeplink && typeof watchedDeeplink !== "string"
      ? watchedDeeplink
      : initialConfig;
  const screen = config.screen;
  const watchedResultParams = Form.useWatch(
    resultParamsPath ?? IDLE_WATCH_PATH,
    {
      form,
      preserve: true,
    },
  ) as Record<string, unknown> | undefined;
  const watchedConditions = Form.useWatch(conditionsPath ?? IDLE_WATCH_PATH, {
    form,
    preserve: true,
  }) as ConditionSpecIn[] | undefined;
  const suggestion =
    resultParamsPath || conditionsPath
      ? suggestBannerDeeplink({
          resultParams: watchedResultParams,
          conditions: watchedConditions,
        })
      : undefined;
  const namesPath = Array.isArray(resultParamsPath)
    ? [...resultParamsPath.slice(0, -1), "restriction_display_names"]
    : IDLE_WATCH_PATH;
  const watchedRestrictionNames = Form.useWatch(namesPath, {
    form,
    preserve: true,
  }) as Record<string, string> | undefined;
  const namesFromResultParams = watchedResultParams?.[
    RESTRICTION_DISPLAY_NAMES_PARAM_KEY
  ] as Record<string, string> | undefined;
  const lookupType =
    suggestion?.kind === "league"
      ? "tournament"
      : suggestion?.kind === "match"
        ? "match"
        : undefined;
  const cachedSearchName = useSportsbookRestrictionNameFromCache(
    lookupType,
    suggestion?.id,
  );
  const cachedCasinoGameName = useRestrictionDisplayName(
    screen === "casino_game" ? valueOf(config.game_id) : undefined,
  );
  const { isEnabled: casinoGameProviderFlag } = usePamFeature(
    "casinoGameDeeplinkProvider",
  );

  const shouldIncludeCasinoGameProviderId = (): boolean => {
    if (casinoGameProviderFlag === true) return true;
    if (casinoGameProviderFlag === false) return false;
    return featureFlagService.isEnabled("casinoGameDeeplinkProvider");
  };

  const dismissedSuggestionIdRef = useRef<string | undefined>(undefined);
  const lastAutoAppliedRef = useRef<AutoAppliedSuggestion | undefined>(
    undefined,
  );
  const prevWatchedDeeplinkRef = useRef<BannerIn["deeplink"] | undefined>(
    undefined,
  );

  const resolveSuggestionDisplayName = (
    suggestionId: string,
  ): string | undefined =>
    usableRestrictionDisplayName(
      suggestionId,
      restrictionDisplayNames?.[suggestionId],
    ) ??
    usableRestrictionDisplayName(
      suggestionId,
      watchedRestrictionNames?.[suggestionId],
    ) ??
    usableRestrictionDisplayName(
      suggestionId,
      namesFromResultParams?.[suggestionId],
    ) ??
    usableRestrictionDisplayName(suggestionId, cachedSearchName) ??
    getRestrictionDisplayName(suggestionId);

  const applySuggestion = (nextSuggestion: DeeplinkSuggestion) => {
    const displayName = resolveSuggestionDisplayName(nextSuggestion.id);
    if (displayName) {
      rememberRestrictionDisplayName(nextSuggestion.id, displayName);
    }

    const nextDeeplink = buildDeeplinkFromSuggestion(
      nextSuggestion,
      shouldIncludeCasinoGameProviderId(),
    );
    form.setFieldValue(deeplinkPath, nextDeeplink);
    form.setFieldValue([...deeplinkPath, "screen"], nextDeeplink.screen);

    if (nextSuggestion.kind === "casino_game") {
      form.setFieldValue([...deeplinkPath, "game_id"], nextDeeplink.game_id);
      if (nextDeeplink.provider_id) {
        form.setFieldValue(
          [...deeplinkPath, "provider_id"],
          nextDeeplink.provider_id,
        );
      } else {
        form.setFieldValue([...deeplinkPath, "provider_id"], undefined);
      }
    } else if (nextSuggestion.kind === "league") {
      form.setFieldValue(
        [...deeplinkPath, "tournament_id"],
        nextDeeplink.tournament_id,
      );
      form.setFieldValue(
        [...deeplinkPath, "sport_id"],
        nextDeeplink.sport_id,
      );
    } else if (nextSuggestion.kind === "match") {
      form.setFieldValue([...deeplinkPath, "event_id"], nextDeeplink.event_id);
    }

    lastAutoAppliedRef.current = {
      kind: nextSuggestion.kind,
      id: nextSuggestion.id,
    };
  };

  useLayoutEffect(() => {
    if (!suggestion) return;

    const currentDeeplink = watchedDeeplink as BannerIn["deeplink"] | undefined;
    const prevDeeplink = prevWatchedDeeplinkRef.current;
    prevWatchedDeeplinkRef.current = currentDeeplink;

    const wasAutoApplied = deeplinkMatchesAutoApplied(
      prevDeeplink,
      lastAutoAppliedRef.current,
    );

    if (!currentDeeplink && wasAutoApplied && lastAutoAppliedRef.current) {
      dismissedSuggestionIdRef.current = lastAutoAppliedRef.current.id;
      lastAutoAppliedRef.current = undefined;
      return;
    }

    if (
      dismissedSuggestionIdRef.current &&
      dismissedSuggestionIdRef.current !== suggestion.id
    ) {
      dismissedSuggestionIdRef.current = undefined;
    }

    if (
      !canAutoApplySuggestion(
        suggestion,
        currentDeeplink ?? initialDeeplink,
        dismissedSuggestionIdRef.current,
        lastAutoAppliedRef.current,
      )
    ) {
      return;
    }

    if (suggestionMatchesDeeplink(suggestion, currentDeeplink ?? initialDeeplink)) {
      lastAutoAppliedRef.current = {
        kind: suggestion.kind,
        id: suggestion.id,
      };
      return;
    }

    applySuggestion(suggestion);
  }, [
    suggestion,
    watchedDeeplink,
    initialDeeplink,
    casinoGameProviderFlag,
    deeplinkPath,
    form,
  ]);

  const itemDeeplinkField = (field: keyof BannerDeeplinkIn): NamePath => [
    ...itemDeeplinkPath,
    field,
  ];
  const deeplinkField = (field: keyof BannerDeeplinkIn): NamePath => [
    ...deeplinkPath,
    field,
  ];

  const handleScreenChange = (nextScreen?: BannerDeeplinkScreen) => {
    form.setFieldValue(
      deeplinkPath,
      nextScreen
        ? {
            screen: nextScreen,
            ...(nextScreen === "deposit" ? { money_type: "cash" } : {}),
          }
        : undefined,
    );
  };

  const handleRemoveDeeplink = () => {
    const dismissId =
      suggestion?.id ?? lastAutoAppliedRef.current?.id;
    if (dismissId) {
      dismissedSuggestionIdRef.current = dismissId;
    }
    lastAutoAppliedRef.current = undefined;
    form.setFieldValue(deeplinkPath, undefined);
  };

  const handleParameterChange = (
    field: keyof BannerDeeplinkIn,
    value?: string,
  ) => {
    const currentDeeplink = form.getFieldValue(
      deeplinkPath,
    ) as BannerIn["deeplink"];
    if (!currentDeeplink || typeof currentDeeplink === "string") return;
    form.setFieldValue(deeplinkPath, {
      ...currentDeeplink,
      [field]: value,
    });
  };

  const setStructuredDeeplink = (nextDeeplink: BannerDeeplinkIn) => {
    form.setFieldValue(deeplinkPath, nextDeeplink);
  };

  const clearCasinoDeeplinkFields = (
    screen: BannerDeeplinkScreen,
    fields: readonly (keyof BannerDeeplinkIn)[],
  ) => {
    const currentDeeplink = form.getFieldValue(
      deeplinkPath,
    ) as BannerIn["deeplink"];
    if (!currentDeeplink || typeof currentDeeplink === "string") return;
    const cleared = { ...currentDeeplink, screen } as BannerDeeplinkIn;
    for (const field of fields) {
      if (field !== "screen") {
        delete cleared[field];
      }
    }
    setStructuredDeeplink(cleared);
  };

  const handleCasinoGameSelect = (item: CasinoDeeplinkDropdownItem) => {
    setStructuredDeeplink({
      screen: "casino_game",
      game_id: item.id,
      ...(shouldIncludeCasinoGameProviderId() && item.provider
        ? { provider_id: item.provider }
        : {}),
    });
  };

  const handleCasinoProviderSelect = (item: CasinoDeeplinkDropdownItem) => {
    setStructuredDeeplink({
      screen: "casino_provider",
      provider_id: item.id,
      eid: CASINO_PROVIDER_DEEPLINK_COLLECTION_SLUG,
      code: CASINO_PROVIDER_DEEPLINK_COLLECTION_SLUG,
    });
  };

  const handleCasinoCollectionSelect = (
    item: CasinoDeeplinkDropdownItem,
    screen: "casino_collection" | "casino_category",
  ) => {
    setStructuredDeeplink({
      screen,
      eid: item.id,
      code: item.id,
    });
  };

  const hiddenDeeplinkField = (
    field: keyof BannerDeeplinkIn,
    options?: {
      dependencies?: NamePath[];
      rules?: Rule[];
    },
  ) => (
    <Form.Item
      key={`hidden-${field}`}
      name={itemDeeplinkField(field)}
      initialValue={legacyInitialValue?.[field]}
      dependencies={options?.dependencies}
      rules={options?.rules}
      hidden
    >
      <Input type="hidden" />
    </Form.Item>
  );

  const requiredRule = (label: string): Rule => ({
    required: true,
    whitespace: true,
    message: formatString(AppStrings.FIELD_IS_REQUIRED, { title: label }),
  });

  const oneOfRule = (
    otherField: keyof BannerDeeplinkIn,
    message: string,
  ): Rule => ({
    validator: (_, currentValue?: string) => {
      if (
        valueOf(currentValue) ||
        valueOf(form.getFieldValue(deeplinkField(otherField)))
      ) {
        return Promise.resolve();
      }
      return Promise.reject(new Error(message));
    },
  });

  const textField = (
    field: keyof BannerDeeplinkIn,
    label: string,
    options?: {
      dependencies?: NamePath[];
      rules?: Rule[];
    },
  ) => (
    <Form.Item
      key={field}
      label={label}
      name={itemDeeplinkField(field)}
      initialValue={legacyInitialValue?.[field]}
      dependencies={options?.dependencies}
      rules={options?.rules}
    >
      <Input
        placeholder={AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_VALUE_PLACEHOLDER}
        onChange={(event) => handleParameterChange(field, event.target.value)}
      />
    </Form.Item>
  );

  const searchField = (
    field: "tournament_id" | "event_id",
    searchType: SportsbookSearchType,
    label: string,
    options?: {
      dependencies?: NamePath[];
      rules?: Rule[];
    },
  ) => (
    <Form.Item
      key={field}
      label={label}
      name={itemDeeplinkField(field)}
      initialValue={legacyInitialValue?.[field]}
      dependencies={options?.dependencies}
      rules={options?.rules}
    >
      <SportsbookDeeplinkIdSelect
        searchType={searchType}
        placeholder={formatString(
          AppStrings.PAM_DYNAMIC_BONUS_SPORTSBOOK_SEARCH_PLACEHOLDER,
          { label: label.toLowerCase() },
        )}
        onChange={(next) => handleParameterChange(field, next)}
      />
    </Form.Item>
  );

  const renderScreenFields = () => {
    switch (screen) {
      case "sportsbook_league":
        return (
          <>
            {searchField(
              "tournament_id",
              "tournament",
              AppStrings.PAM_DYNAMIC_BONUS_TOURNAMENT_ID,
              {
                dependencies: [deeplinkField("sport_id")],
                rules: [
                  oneOfRule(
                    "sport_id",
                    AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_LEAGUE_ID_REQUIRED,
                  ),
                ],
              },
            )}
            {textField("sport_id", AppStrings.PAM_DYNAMIC_BONUS_SPORT_ID)}
          </>
        );
      case "sportsbook_match":
        return searchField("event_id", "match", AppStrings.EVENT_ID, {
          rules: [requiredRule(AppStrings.EVENT_ID)],
        });
      case "deposit":
        return (
          <Form.Item
            label={AppStrings.MONEY_TYPE}
            name={itemDeeplinkField("money_type")}
            initialValue={legacyInitialValue?.money_type ?? "cash"}
            rules={[requiredRule(AppStrings.MONEY_TYPE)]}
          >
            <Select<"cash" | "coin">
              options={[
                { label: AppStrings.CASH, value: "cash" },
                { label: AppStrings.COIN, value: "coin" },
              ]}
              onChange={(value) => handleParameterChange("money_type", value)}
            />
          </Form.Item>
        );
      case "casino_game": {
        const casinoGameId = valueOf(config.game_id);
        const casinoGameDisplayName = casinoGameId
          ? (usableRestrictionDisplayName(
              casinoGameId,
              restrictionDisplayNames?.[casinoGameId],
            ) ??
            usableRestrictionDisplayName(
              casinoGameId,
              watchedRestrictionNames?.[casinoGameId],
            ) ??
            usableRestrictionDisplayName(
              casinoGameId,
              namesFromResultParams?.[casinoGameId],
            ) ??
            getRestrictionDisplayName(casinoGameId) ??
            cachedCasinoGameName)
          : undefined;
        return (
          <>
            <CasinoDeeplinkSearchField
              searchType="game"
              label={AppStrings.GAME_ID}
              value={valueOf(config.game_id)}
              displayName={casinoGameDisplayName}
              onSelect={handleCasinoGameSelect}
              onClear={() =>
                clearCasinoDeeplinkFields("casino_game", [
                  "game_id",
                  "provider_id",
                ])
              }
            />
            {hiddenDeeplinkField("game_id", {
              rules: [requiredRule(AppStrings.GAME_ID)],
            })}
            {hiddenDeeplinkField("provider_id")}
          </>
        );
      }
      case "casino_provider":
        return (
          <>
            <CasinoDeeplinkSearchField
              searchType="provider"
              label={AppStrings.PAM_DYNAMIC_BONUS_PROVIDER_ID}
              value={valueOf(config.provider_id)}
              onSelect={handleCasinoProviderSelect}
              onClear={() =>
                clearCasinoDeeplinkFields("casino_provider", [
                  "provider_id",
                  "eid",
                  "code",
                ])
              }
            />
            {hiddenDeeplinkField("provider_id", {
              rules: [requiredRule(AppStrings.PAM_DYNAMIC_BONUS_PROVIDER_ID)],
            })}
            {hiddenDeeplinkField("eid", {
              dependencies: [deeplinkField("code")],
              rules: [
                oneOfRule(
                  "code",
                  AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_CATALOG_ID_REQUIRED,
                ),
              ],
            })}
            {hiddenDeeplinkField("code", {
              dependencies: [deeplinkField("eid")],
              rules: [
                oneOfRule(
                  "eid",
                  AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_CATALOG_ID_REQUIRED,
                ),
              ],
            })}
          </>
        );
      case "casino_collection":
      case "casino_category": {
        const catalogSearchType =
          screen === "casino_category" ? "category" : "collection";
        return (
          <>
            <CasinoDeeplinkSearchField
              searchType={catalogSearchType}
              label={AppStrings.CODE}
              value={valueOf(config.code) ?? valueOf(config.eid)}
              onSelect={(item) =>
                handleCasinoCollectionSelect(
                  item,
                  screen as "casino_collection" | "casino_category",
                )
              }
              onClear={() =>
                clearCasinoDeeplinkFields(screen, ["eid", "code"])
              }
            />
            {hiddenDeeplinkField("eid", {
              dependencies: [deeplinkField("code")],
              rules: [
                oneOfRule(
                  "code",
                  AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_CATALOG_ID_REQUIRED,
                ),
              ],
            })}
            {hiddenDeeplinkField("code", {
              dependencies: [deeplinkField("eid")],
              rules: [
                oneOfRule(
                  "eid",
                  AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_CATALOG_ID_REQUIRED,
                ),
              ],
            })}
          </>
        );
      }
      case "poker_table":
        return textField("game_id", AppStrings.GAME_ID);
      default:
        return null;
    }
  };

  const legacyDeeplink =
    typeof initialDeeplink === "string" && !initialConfig.screen
      ? initialDeeplink
      : undefined;

  const hasDeeplink = Boolean(screen || legacyDeeplink);

  const screenLabel = (
    <span className={styles.deeplinkScreenLabel}>
      <span className={styles.deeplinkScreenLabelRow}>
        <span className={styles.deeplinkScreenLabelText}>
          {AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_LABEL}
        </span>
        {hasDeeplink ? (
          <button
            type="button"
            className={styles.deeplinkRemoveTrigger}
            data-testid="deeplink-remove"
            onClick={(event) => {
              event.preventDefault();
              handleRemoveDeeplink();
            }}
          >
            <DeleteOutlined aria-hidden />
            {AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_REMOVE}
          </button>
        ) : null}
      </span>
    </span>
  );

  return (
    <div className={styles.deeplinkBuilder}>
      <Form.Item
        className={styles.deeplinkScreenItem}
        colon={false}
        label={screenLabel}
        name={itemDeeplinkField("screen")}
        initialValue={legacyInitialValue?.screen}
      >
        <Select<BannerDeeplinkScreen>
          allowClear
          showSearch
          optionFilterProp="label"
          options={SCREEN_OPTIONS()}
          placeholder={AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_SCREEN_PLACEHOLDER}
          onChange={handleScreenChange}
        />
      </Form.Item>

      {legacyDeeplink && !screen ? (
        <Form.Item label={AppStrings.PAM_DYNAMIC_BONUS_DEEPLINK_LABEL}>
          <Input disabled value={legacyDeeplink} />
        </Form.Item>
      ) : null}

      {screen ? (
        <div className={styles.deeplinkParameterGrid}>
          {renderScreenFields()}
        </div>
      ) : null}
    </div>
  );
};

export default BannerDeeplinkField;
